import React, { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { Button } from '../ui';
import { useAuth } from '../../context/AuthContext';
import { navigateTo } from '../../navigation/rootNavigation';
import { setAgapellaAccess, refreshAgapellaAccess, useAgapellaAccess } from '../../services/agapellaAccess';
import {
  agapellaPaywallCopy,
  agapellaPaywallMode,
  closeAgapellaPaywall,
  useAgapellaPaywallRequest,
  type AgapellaPaywallRequest,
} from '../../services/agapellaGate';
import { analytics } from '../../services/analytics';
import { api } from '../../services/api';
import { useCanShowExternalPurchase } from '../../services/paymentSurface';
import { useCanBuyInStore } from '../../services/useCanBuyInStore';
import { trialStartErrorLine } from '../../screens/subscription/agapellaSubscription';
import type { AgapellaSubscriptionOffer } from '../../types/agapellaSubscription';
import { TrialCard } from './AgapellaTrialGate';

const IS_WEB = Platform.OS === 'web';

/**
 * CORE-1776 — окно «Входит в Agapēlla» (страница CORE-662, раздел 4.2).
 *
 * Смонтировано один раз рядом с `AgapellaTrialGate`; экраны его не рисуют, а
 * зовут `openAgapellaPaywall` / `handleAgapellaDenial`. Показывается, когда
 * человек нажал «Смотреть» или «Войти», а не при открытии карточки.
 *
 * Про цену и оплату — только там, где можно (CORE-1535): Android вне РФ и
 * iOS не видят ни цены, ни кнопки, ни слов о сайте. Пробный доступ —
 * бесплатный, его предлагаем везде.
 *
 * CORE-1773 — вне РФ на iOS и Android «Оформить подписку» ведёт к покупке в
 * сторе: карточка в «Настройках» показывает цену стора и условия продления.
 */
export const AgapellaPaywallHost: React.FC = () => {
  const req = useAgapellaPaywallRequest();
  if (!req) return null;
  return <AgapellaPaywall req={req} />;
};

const AgapellaPaywall: React.FC<{ req: AgapellaPaywallRequest }> = ({ req }) => {
  const { isAuthenticated } = useAuth();
  const state = useAgapellaAccess();
  const externalPurchase = useCanShowExternalPurchase();
  const storePurchase = useCanBuyInStore();
  const [offer, setOffer] = useState<AgapellaSubscriptionOffer | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (externalPurchase !== true || !isAuthenticated) return;
    let alive = true;
    void api.getAgapellaSubscriptionOffer()
      .then((o) => { if (alive) setOffer(o); })
      .catch(() => { if (alive) setOffer(null); });
    return () => { alive = false; };
  }, [externalPurchase, isAuthenticated]);

  // Пока не решено, будет ли кнопка оплаты, окна нет: иначе она появилась бы
  // в уже открытом окне. Состояние доступа не ждём: `handleAgapellaDenial` его
  // уже перечитал, а не ответил сервер — окно всё равно должно объяснить отказ.
  const deciding = isAuthenticated
    && (externalPurchase === null || storePurchase === null || (externalPurchase && offer === undefined));
  const mode = agapellaPaywallMode(isAuthenticated, state, externalPurchase, offer ?? null, storePurchase);

  useEffect(() => {
    if (deciding) return;
    analytics.track('agapella_paywall_shown', { surface: req.surface, kind: req.kind, mode, platform: Platform.OS });
  }, [deciding, mode, req]);

  const close = useCallback((action: string) => {
    analytics.track('agapella_paywall_cta', { surface: req.surface, kind: req.kind, mode, action, platform: Platform.OS });
    closeAgapellaPaywall();
  }, [mode, req]);

  const startTrial = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await api.startAgapellaTrial();
      analytics.track('agapella_trial_started', { platform: Platform.OS, surface: `paywall:${req.surface}` });
      setAgapellaAccess(next);
      close('trial');
      req.onUnlocked?.();
    } catch (e) {
      setError(trialStartErrorLine(e));
      // Отказ «уже был» значит, что наше состояние устарело — перечитать.
      void refreshAgapellaAccess();
    } finally {
      setBusy(false);
    }
  }, [close, req]);

  const primary = useCallback(() => {
    if (mode === 'trial') { void startTrial(); return; }
    if (mode === 'guest') {
      close('login');
      const returnTo = IS_WEB ? window.location.pathname + window.location.search : undefined;
      navigateTo('Login', { returnTo });
      return;
    }
    if (mode === 'pay') {
      close('pay');
      // Веб — страница подписки; приложение — карточка в «Настройках», она же
      // открывает внешний браузер и встречает возврат с оплаты (CORE-1537).
      if (IS_WEB) navigateTo('AgapellaSubscription');
      else navigateTo('Settings', { checkout: true });
      return;
    }
    if (mode === 'store') {
      close('store');
      navigateTo('Settings');
      return;
    }
    close('ok');
    req.onDismiss?.();
  }, [close, mode, startTrial, req]);

  if (deciding) return null;
  const copy = agapellaPaywallCopy(req.kind, mode, offer ?? null);
  const skip = () => { close('skip'); req.onDismiss?.(); };

  return (
    <TrialCard testID="agapella-paywall" title={copy.title} lines={copy.lines} error={error} onClose={skip}>
      <Button
        variant="solar"
        size="lg"
        isLoading={busy}
        disabled={busy}
        onPress={primary}
        testID={`agapella-paywall-${mode}`}
      >
        {copy.primary}
      </Button>
      {copy.secondary && (
        <Button variant="ghost" disabled={busy} onPress={skip} testID="agapella-paywall-skip">
          {copy.secondary}
        </Button>
      )}
    </TrialCard>
  );
};
